import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'

dayjs.extend(relativeTime)

/**
 * Format a date as relative time (e.g., "2 minutes ago", "1 hour ago")
 * @param {Date|string} date - The date to format
 * @returns {string} The formatted relative time
 */
export function formatRelativeTime (date) {
  if (!date) return ''
  return dayjs(date).fromNow()
}

/**
 * Format a duration in milliseconds to a human-readable string
 * @param {number} milliseconds - Duration in milliseconds
 * @returns {string} Formatted duration (e.g., "1.5s", "2m 30s")
 */
export function formatDuration (milliseconds) {
  if (!milliseconds && milliseconds !== 0) return ''

  if (milliseconds < 1000) {
    return `${milliseconds}ms`
  }

  const seconds = Math.floor(milliseconds / 1000)
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60

  if (minutes > 0) {
    return remainingSeconds > 0
      ? `${minutes}m ${remainingSeconds}s`
      : `${minutes}m`
  }

  // For seconds, show one decimal place if less than 10 seconds
  if (seconds < 10) {
    return `${(milliseconds / 1000).toFixed(1)}s`
  }

  return `${seconds}s`
}

/**
 * Format a number to a specific number of decimal places
 * @param {number} num - The number to format
 * @param {number} decimals - Number of decimal places
 * @returns {string} The formatted number
 */
export function formatNumber (num, decimals = 2) {
  if (num === null || num === undefined) return ''
  return Number(num).toFixed(decimals)
}

/**
 * Format a number as currency
 * @param {number} amount - The amount to format
 * @param {string} currency - Currency code (default: 'USD')
 * @returns {string} The formatted currency string
 */
export function formatCurrency (amount, currency = 'USD') {
  if (amount === null || amount === undefined) return ''

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency
  }).format(amount)
}

/**
 * Format a number as a percentage
 * @param {number} value - The value to format (0-1 or 0-100)
 * @param {boolean} isDecimal - Whether the value is already a decimal (0-1)
 * @param {number} decimals - Number of decimal places
 * @returns {string} The formatted percentage
 */
export function formatPercentage (value, isDecimal = true, decimals = 2) {
  if (value === null || value === undefined) return ''

  const percentage = isDecimal ? value * 100 : value
  return `${percentage.toFixed(decimals)}%`
}

/**
 * Format bytes to human-readable size
 * @param {number} bytes - Size in bytes
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted size (e.g., "1.5 MB")
 */
export function formatBytes (bytes, decimals = 2) {
  if (bytes === 0) return '0 Bytes'
  if (!bytes) return ''

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB']

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
}

/**
 * Truncate text to a maximum length with ellipsis
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length
 * @param {string} suffix - Suffix to add (default: '...')
 * @returns {string} Truncated text
 */
export function truncateText (text, maxLength, suffix = '...') {
  if (!text || text.length <= maxLength) return text
  return text.substring(0, maxLength - suffix.length) + suffix
}

/**
 * Format a date to a specific format
 * @param {Date|string} date - The date to format
 * @param {string} format - dayjs format string (default: 'MMM D, YYYY h:mm A')
 * @returns {string} Formatted date
 */
export function formatDate (date, format = 'MMM D, YYYY h:mm A') {
  if (!date) return ''
  return dayjs(date).format(format)
}

/**
 * Formats database identifiers into human-readable display names.
 * Converts snake_case and camelCase to Title Case and handles common abbreviations.
 *
 * @param {string} identifier - Database identifier (table or column name)
 * @returns {string} Formatted display name
 *
 * @example
 * formatDisplayName('client_roi') // Returns 'Client ROI'
 * formatDisplayName('bppReportId') // Returns 'BPP Report ID'
 * formatDisplayName('mgd_value') // Returns 'Managed Value'
 */
export function formatDisplayName (identifier) {
  if (!identifier) return ''

  // Handle both snake_case and camelCase
  // First, split on underscores, then split each part on camelCase boundaries
  const words = identifier
    .split('_')
    .flatMap(part => {
      // Split camelCase words, handling consecutive capitals properly
      // e.g., "clientROI" -> ["client", "ROI"], "ID" -> ["ID"]
      return part
        .split(/(?<=[a-z])(?=[A-Z])|(?<=[A-Z])(?=[A-Z][a-z])/)
        .filter(word => word.length > 0)
    })

  return words
    .map(word => {
      // Handle common abbreviations
      const abbreviations = {
        id: 'ID',
        roi: 'ROI',
        mgd: 'Managed',
        inv: 'Investment',
        mtg: 'Meeting',
        bpp: 'BPP'
      }

      const lower = word.toLowerCase()
      if (abbreviations[lower]) {
        return abbreviations[lower]
      }

      // Capitalize first letter
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    })
    .join(' ')
}

/**
 * Format a value as 'Yes' / 'No' / em-dash for display in tables and read-only
 * detail panels. Lenient truthiness: any truthy value renders 'Yes', any falsy
 * value renders 'No', BUT null/undefined always render as the em-dash placeholder
 * to indicate "no data" distinctly from an explicit false.
 *
 * Designed to handle DB columns that are nominally boolean but stored as
 * TINYINT (0/1) or INT (orionSleeve), where 0 means "no" and any positive
 * value means "yes".
 *
 * @param {*} value
 * @returns {string} 'Yes' | 'No' | '—'
 *
 * @example
 * formatYesNo(true)       // 'Yes'
 * formatYesNo(false)      // 'No'
 * formatYesNo(1)          // 'Yes'
 * formatYesNo(0)          // 'No'
 * formatYesNo(null)       // '—'
 * formatYesNo(undefined)  // '—'
 * formatYesNo('yes')      // 'Yes' (any truthy string)
 * formatYesNo('')         // 'No' (empty string is falsy)
 */
export function formatYesNo (value) {
  if (value === null || value === undefined) return '—'
  return value ? 'Yes' : 'No'
}
